import { useState, useEffect, useMemo } from 'react';

export interface Permission {
  id: number;
  permissionCode: string;
  permissionName: string;
  permissionType: 'CREATE' | 'UPDATE' | 'VIEW' | 'DELETE';
  featureVO: {
    id: number;
    featureCode: string;
    featureName: string;
  };
}

export interface UserPermissions {
  permissions: Permission[];
  hasPermission: (permissionCode: string) => boolean;
  hasFeaturePermission: (featureCode: string, permissionType: 'CREATE' | 'UPDATE' | 'VIEW' | 'DELETE') => boolean;
  hasAnyPermission: (permissionCodes: readonly string[]) => boolean;
  hasAllPermissions: (permissionCodes: readonly string[]) => boolean;
  getPermissionsByFeature: (featureCode: string) => Permission[];
  getPermissionsByType: (permissionType: 'CREATE' | 'UPDATE' | 'VIEW' | 'DELETE') => Permission[];
}

export function usePermissions(): UserPermissions {
  const [permissions, setPermissions] = useState<Permission[]>([]);

  useEffect(() => {
    const storedPermissions = sessionStorage.getItem('user-permissions');
    if (storedPermissions) {
      try {
        const parsedPermissions = JSON.parse(storedPermissions);
        setPermissions(Array.isArray(parsedPermissions) ? parsedPermissions : []);
      } catch (error) {
        console.error('Error parsing user permissions:', error);
        setPermissions([]);
      }
    }
  }, []);

  const hasPermission = useMemo(() => {
    return (permissionCode: string): boolean => {
      return permissions.some(permission => permission.permissionCode === permissionCode);
    };
  }, [permissions]);

  const hasFeaturePermission = useMemo(() => {
    return (featureCode: string, permissionType: 'CREATE' | 'UPDATE' | 'VIEW' | 'DELETE'): boolean => {
      return permissions.some(permission => 
        permission.featureVO.featureCode === featureCode && 
        permission.permissionType === permissionType
      );
    };
  }, [permissions]);

  const hasAnyPermission = useMemo(() => {
    return (permissionCodes: readonly string[]): boolean => {
      return permissionCodes.some(code => hasPermission(code));
    };
  }, [hasPermission]);

  const hasAllPermissions = useMemo(() => {
    return (permissionCodes: readonly string[]): boolean => {
      return permissionCodes.every(code => hasPermission(code));
    };
  }, [hasPermission]);

  const getPermissionsByFeature = useMemo(() => {
    return (featureCode: string): Permission[] => {
      return permissions.filter(permission => permission.featureVO.featureCode === featureCode);
    };
  }, [permissions]);

  const getPermissionsByType = useMemo(() => {
    return (permissionType: 'CREATE' | 'UPDATE' | 'VIEW' | 'DELETE'): Permission[] => {
      return permissions.filter(permission => permission.permissionType === permissionType);
    };
  }, [permissions]);

  return {
    permissions,
    hasPermission,
    hasFeaturePermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissionsByFeature,
    getPermissionsByType,
  };
} 